<template>
    <form
        class="relative block w-full"
        v-click-outside="() => (hideSuggestions = true)"
    >
        <label class="relative block w-full">
            <span class="sr-only">Search</span>
            <input
                type="text"
                v-model="model"
                class="block w-full bg-white dark:bg-slate-800 rounded border-none py-1.5 text-slate-500 dark:text-slate-400 text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                :placeholder="placeholder"
                v-debounce="350"
                @input="handleChange"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown.enter.prevent="handleSubmit"
                @keydown.down="navigate(1)"
                @keydown.up="navigate(-1)"
                autocomplete="off"
            />
            <button
                class="absolute right-2 top-1/2 transform -translate-y-1/2 z-10"
                @click="handleClear"
                v-if="model"
            >
                <Icon icon="close" class="text-slate-500 dark:text-slate-400" />
            </button>
        </label>
        <div
            class="absolute left-0 right-0 top-full z-20 pt-2"
            v-if="
                (filteredSuggestions.length || isSearching) &&
                inputFocused &&
                !hideSuggestions
            "
        >
            <ul
                class="w-full sm:min-w-max max-h-40 min-h-16 overflow-y-auto p-2 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/50 relative"
            >
                <li
                    class="w-full flex items-center justify-center p-4"
                    v-if="isSearching"
                >
                    <svg
                        class="animate-spin h-5 w-5 text-slate-500 dark:text-slate-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                </li>
                <li
                    v-for="(suggestion, index) in filteredSuggestions"
                    :key="suggestion.code"
                    @mousedown.prevent="selectSuggestion(index)"
                    class="w-full cursor-pointer p-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors gap-2 rounded-md flex items-center justify-between"
                    :class="{ 'bg-slate-100 dark:bg-slate-700': index === selectedIndex }"
                    role="button"
                    v-if="!isSearching"
                >
                    <div
                        class="w-5 h-5 justify-center items-center flex text-slate-800 dark:text-slate-100 dark:text-slate-200"
                        v-if="dropdownName === 'airport'"
                    >
                        <div
                            class="w-5 h-5 relative flex-col justify-start items-start flex"
                        >
                            <LoungeIcon
                                icon="airplane"
                                v-if="!suggestion.recentSearch"
                            />
                            <Icon icon="history" v-else />
                        </div>
                    </div>
                    <div
                        class="grow shrink basis-0 flex-col justify-center items-start gap-1 flex"
                    >
                        <div
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-tight"
                        >
                            {{ suggestion[displayKey] }}
                        </div>
                        <div
                            class="self-stretch text-slate-500 dark:text-slate-400 text-xs font-normal leading-none"
                            v-if="dropdownName === 'airport'"
                        >
                            {{ suggestion.city }},
                            {{ suggestion.country }}
                        </div>
                    </div>
                </li>
                <li
                    class="w-full p-4 gap-2 rounded-md flex"
                    v-if="!isSearching && !filteredSuggestions.length && model"
                >
                    No results found, try adjusting your search.
                </li>
            </ul>
        </div>
    </form>
</template>
<script setup>
import { computed, ref, watch } from 'vue';
import vClickOutside from 'click-outside-vue3';
import LoungeIcon from '@/components/tools/lounges/partials/lounge-icon/lounge-icon.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { Apii } from '@/services/api';

const ApiService = new Apii();

const props = defineProps({
    recentSearches: {
        type: Array,
        required: false,
        default: () => [],
    },
    staticSuggestions: {
        type: Array,
        required: false,
        default: () => [],
    },
    inputValue: {
        type: String,
        required: false,
    },
    placeholder: {
        type: String,
        default: 'Search by airport code or name',
    },
    dropdownName: {
        type: String,
        default: 'airport',
    },
    displayKey: {
        type: String,
        default: 'name',
    },
    valueKey: {
        type: String,
        default: 'value',
    },
});

const useStaticSearch = computed(() => props.staticSuggestions.length > 0);

const emit = defineEmits(['submit', 'clear', 'select', 'change']);
const model = ref('');
const selectedIndex = ref(-1);
const inputFocused = ref(false);
const isSearching = ref(false);
const searchResults = ref([]);
const hideSuggestions = ref(false);

const filteredSuggestions = computed(() => {
    const uniqueMap = new Map();

    // Add recent searches first (marked as recent)
    props.recentSearches.forEach((item) => {
        uniqueMap.set(item[props.valueKey], { ...item, recentSearch: true });
    });

    // Determine source: static suggestions or API search results
    const sourceItems = useStaticSearch.value
        ? props.staticSuggestions
        : searchResults.value;

    // Add source items (not marked as recent, unless already in map)
    sourceItems.forEach((item) => {
        if (!uniqueMap.has(item[props.valueKey])) {
            uniqueMap.set(item[props.valueKey], {
                ...item,
                recentSearch: false,
            });
        }
    });

    const allItems = Array.from(uniqueMap.values());

    // For static suggestions, filter client-side
    if (useStaticSearch.value && model.value) {
        const s = model.value.toLowerCase();
        const filtered = allItems.filter(
            (item) =>
                item[props.displayKey]?.toLowerCase().includes(s) ||
                item[props.valueKey]?.toLowerCase().includes(s)
        );
        return filtered.sort((a, b) => {
            if (a.recentSearch !== b.recentSearch) {
                return a.recentSearch ? -1 : 1;
            }
            const aCode = a[props.valueKey]?.toLowerCase() || '';
            const bCode = b[props.valueKey]?.toLowerCase() || '';
            if (aCode === s && bCode !== s) return -1;
            if (aCode !== s && bCode === s) return 1;
            if (aCode.startsWith(s) && !bCode.startsWith(s)) return -1;
            if (bCode.startsWith(s) && !aCode.startsWith(s)) return 1;
            return aCode.localeCompare(bCode);
        });
    }

    // For API search, sort results
    if (model.value) {
        const s = model.value.toLowerCase();
        return allItems.sort((a, b) => {
            if (a.recentSearch !== b.recentSearch) {
                return a.recentSearch ? -1 : 1;
            }
            const aCode = a[props.valueKey]?.toLowerCase() || '';
            const bCode = b[props.valueKey]?.toLowerCase() || '';
            if (aCode === s && bCode !== s) return -1;
            if (aCode !== s && bCode === s) return 1;
            if (aCode.startsWith(s) && !bCode.startsWith(s)) return -1;
            if (bCode.startsWith(s) && !aCode.startsWith(s)) return 1;
            return aCode.localeCompare(bCode);
        });
    }

    // When no search query, show recent searches only (for API mode) or all items (for static mode)
    return useStaticSearch.value
        ? allItems
        : allItems.filter((item) => item.recentSearch);
});

const searchAirports = async () => {
    if (!model.value || model.value.trim() === '') {
        searchResults.value = [];
        return;
    }

    isSearching.value = true;
    try {
        const response = await ApiService.apiCall(
            `/airports/search?search=${encodeURIComponent(model.value.trim())}`,
            'get'
        );
        searchResults.value = response.data || [];
        if (searchResults.value.length > 0) {
            selectedIndex.value = 0;
        }
    } catch (error) {
        console.error('Airport search error:', error);
        searchResults.value = [];
    } finally {
        isSearching.value = false;
    }
};

const selectSuggestion = (index = selectedIndex.value) => {
    if (index >= 0 && index < filteredSuggestions.value.length) {
        emit('select', filteredSuggestions.value[index]);
        model.value = '';
        selectedIndex.value = -1;
        inputFocused.value = false;
    }
};
const handleChange = () => {
    emit('change', model.value);
    hideSuggestions.value = false;
    // Only call API search if not using static suggestions
    if (!useStaticSearch.value) {
        searchAirports();
    }
};

const handleClear = () => {
    model.value = '';
    selectedIndex.value = -1;
    emit('clear', model.value);
};

const handleBlur = () => {
    inputFocused.value = false;
};

const handleFocus = () => {
    selectedIndex.value = 0;
    inputFocused.value = true;
};

const handleSubmit = () => {
    selectSuggestion();
};

const navigate = (direction) => {
    if (!filteredSuggestions.value.length) return;
    selectedIndex.value =
        (selectedIndex.value + direction + filteredSuggestions.value.length) %
        filteredSuggestions.value.length;
};

watch(
    () => props.inputValue,
    () => {
        model.value = props.inputValue;
    },
    {
        immediate: true,
    }
);
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
